import { useContentfulImages } from '@snapchat/mw-contentful-client';
import {
  type ImageSources,
  Alignment,
  BackgroundColor,
  Block as BlockSDS,
} from '@snapchat/snap-design-system-marketing';
import type { FC, ReactNode } from 'react';

import { Content } from '../../../../components/Content';
import { getImageSourceSettings } from '../../../../components/Image';
import type { ContentfulIdVariable } from '../../../../hooks/useContentfulQuery';
import { useContentfulQuery } from '../../../../hooks/useContentfulQuery';
import { useParseMediaContainer } from '../../../../hooks/useParseMediaContainer';
import { combineImageSources } from '../../../../utils/combineImageSources';
import { renderRichTextMarkingsOnly } from '../../../../utils/renderText/renderRichText';
import { RegistrationForm } from './RegistrationForm';
import type { RegistrationBlockData } from './RegistrationFormBlockQuery';
import { query } from './RegistrationFormBlockQuery';

export const RegistrationFormBlock: FC<{
  id: string;
  preChildren?: ReactNode;
  postChildren?: ReactNode;
}> = props => {
  const { data } = useContentfulQuery<RegistrationBlockData, ContentfulIdVariable>(query, {
    variables: { id: props.id },
  });

  const backgroundMediaV2 = data?.registrationFormBlock?.backgroundMediaV2;

  const {
    media: { imageSource: backgroundImageSource, videoSource: backgroundVideoSource },
    mobileMedia: {
      imageSource: mobileBackgroundImageSource,
      videoSource: mobileBackgroundVideoSource,
    },
    thumbnailSource,
  } = useParseMediaContainer(backgroundMediaV2);

  const { getImageSources } = useContentfulImages();

  if (!data?.registrationFormBlock) return null;

  const {
    eyebrow,
    title,
    subtitle,
    titleAlignmentDesktop = Alignment.Center,
    titleAlignmentMobile = Alignment.Start,
    backgroundColor = BackgroundColor.White,
    fullHeight,
    formRowsCollection = { items: [] },
    additionalFeaturesCollection,
    formAnalytics = {
      label: 'Submit',
      sys: { id: `form-analytics-${props.id}` },
    },
    connector,
    rememberRegistration,
    confirmationSlug: slug,
    unknownErrorText,
    sideContent,
  } = data.registrationFormBlock ?? {};

  let bgImgSrc: ImageSources | undefined;

  if (backgroundMediaV2?.__typename === 'Image') {
    const { desktopSettings, mobileSettings } = getImageSourceSettings({
      desktopHeight: backgroundMediaV2.media?.height ?? 0,
      mobileHeight: backgroundMediaV2.mobileMedia?.height,
      enableHighDpi: backgroundMediaV2.enableHighDpi,
      quality: backgroundMediaV2.quality,
    });

    bgImgSrc = combineImageSources({
      desktop: getImageSources(backgroundImageSource, desktopSettings),
      mobile: getImageSources(mobileBackgroundImageSource, mobileSettings),
    });
  }

  return (
    <BlockSDS
      eyebrow={eyebrow}
      title={renderRichTextMarkingsOnly(title)}
      subtitle={renderRichTextMarkingsOnly(subtitle)}
      titleAlignment={titleAlignmentDesktop}
      titleAlignmentMobile={titleAlignmentMobile}
      backgroundColor={backgroundColor}
      backgroundVideoSource={backgroundVideoSource}
      backgroundPosterSource={thumbnailSource}
      mobileBackgroundVideoSource={mobileBackgroundVideoSource}
      backgroundImageSources={bgImgSrc}
      fullHeight={fullHeight}
      preChildren={props.preChildren}
      postChildren={props.postChildren}
    >
      {sideContent && <Content {...sideContent} />}
      <RegistrationForm
        id={props.id}
        formAnalytics={formAnalytics}
        formRowsCollection={formRowsCollection}
        additionalFeaturesCollection={additionalFeaturesCollection}
        rememberRegistration={rememberRegistration === true}
        confirmationUrl={slug?.slug ?? ''}
        unknownErrorText={unknownErrorText!}
        connector={connector!}
      />
    </BlockSDS>
  );
};
